import React, { useEffect, useState, useRef } from 'react';
import { subscriptionApi } from '../../services/subscriptionApi';

declare global {
  interface Window {
    paypal?: {
      Buttons: (options: {
        style?: { layout?: string };
        createSubscription: (
          data: unknown,
          actions: {
            subscription: {
              create: (opts: {
                plan_id: string;
                application_context?: { shipping_preference?: string };
              }) => Promise<string>;
            };
          }
        ) => Promise<string>;
        onApprove: (data: { subscriptionID?: string }) => void;
        onCancel: () => void;
        onError: (err: unknown) => void;
      }) => { render: (selector: string | HTMLElement) => Promise<void> };
    };
  }
}

type PayPalConfig = { clientId: string; planId: string };

interface PayPalSubscriptionButtonsProps {
  onApprove?: (data: { subscriptionID: string }) => void;
  /** Optional text shown above the buttons (e.g. plan summary). */
  planSummary?: string;
}

function loadPayPalScript(clientId: string): Promise<void> {
  if (window.paypal) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&locale=en_US`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayPal script'));
    document.body.appendChild(script);
  });
}

const PayPalSubscriptionButtons: React.FC<PayPalSubscriptionButtonsProps> = ({
  onApprove,
  planSummary,
}) => {
  const [config, setConfig] = useState<PayPalConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const buttonsRenderedRef = useRef(false);

  // Fetch config on mount (authenticated; planId is trial vs no-trial based on user)
  useEffect(() => {
    let cancelled = false;
    subscriptionApi
      .getConfig()
      .then((data) => {
        if (cancelled) return;
        const id = data?.clientId?.trim?.();
        const plan = data?.planId?.trim?.();
        if (id && plan) {
          setConfig({ clientId: id, planId: plan });
          setConfigError(null);
        } else {
          setConfigError('PayPal is not configured.');
        }
      })
      .catch(() => {
        if (!cancelled) setConfigError('Failed to load PayPal.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load PayPal script when we have config
  useEffect(() => {
    if (!config?.clientId || scriptLoaded || scriptError) return;
    let cancelled = false;
    loadPayPalScript(config.clientId)
      .then(() => {
        if (!cancelled) setScriptLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setScriptError('Failed to load PayPal.');
      });
    return () => {
      cancelled = true;
    };
  }, [config?.clientId, scriptLoaded, scriptError]);

  // Render PayPal buttons when script is loaded and we have planId and container
  useEffect(() => {
    if (!config?.planId || !scriptLoaded || !window.paypal || !buttonContainerRef.current || buttonsRenderedRef.current) return;

    const planId = config.planId;
    buttonsRenderedRef.current = true;

    window.paypal
      .Buttons({
        style: { layout: 'vertical' },
        createSubscription: (_data, actions) =>
          actions.subscription.create({
            plan_id: planId,
            application_context: { shipping_preference: 'NO_SHIPPING' },
          }),
        onApprove: (data) => {
          try {
            if (data.subscriptionID) {
              onApprove?.({ subscriptionID: data.subscriptionID });
            }
          } catch {
            // Swallow so cross-origin script errors don't bubble
          }
        },
        onCancel: () => {
          // No modal to close; optional future callback
        },
        onError: (err) => {
          try {
            console.error('PayPal subscription error:', err);
          } catch {
            // no-op
          }
        },
      })
      .render(buttonContainerRef.current)
      .catch((err) => {
        console.error('PayPal render error:', err);
        buttonsRenderedRef.current = false;
      });

    return () => {
      buttonsRenderedRef.current = false;
      const container = buttonContainerRef.current;
      window.setTimeout(() => {
        try {
          if (container) {
            container.innerHTML = '';
          }
        } catch {
          // Ignore errors from PayPal when clearing the container
        }
      }, 150);
    };
  }, [config?.planId, scriptLoaded, onApprove]);

  const showButtons = config?.clientId && config?.planId && scriptLoaded && !configError && !scriptError;

  return (
    <div className="w-full">
      {planSummary && (
        <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-4">
          {planSummary}
        </p>
      )}
      {configError || scriptError ? (
        <p className="text-red-600 dark:text-red-400 text-sm">
          {configError || scriptError}
        </p>
      ) : showButtons ? (
        <div ref={buttonContainerRef} />
      ) : (
        <p className="text-text-light-secondary dark:text-text-secondary text-sm py-2">
          Loading PayPal…
        </p>
      )}
    </div>
  );
};

export default PayPalSubscriptionButtons;
